import React, { useRef, useState } from 'react';
import { Upload, Trash2, GripVertical, Loader2, X, CheckCircle, AlertCircle } from 'lucide-react';
import { ImageManagementProps, UploadingImage } from './types';
import { apiService } from '../../../services/api';

function ImageManagementSection({
  course,
  existingImages,
  setExistingImages,
  uploadingImages,
  setUploadingImages,
  deletingImageIds,
  setDeletingImageIds,
  isReordering,
  setIsReordering,
  error,
  setError
}: ImageManagementProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedImageIndex, setDraggedImageIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalImageCount = existingImages.length + uploadingImages.filter(img => img.status !== 'error').length;

  // 加载现有图片
  const loadExistingImages = async (courseId: number) => {
    try {
      const response = await fetch(`/api/admin/golf-courses/${courseId}/images`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExistingImages(data.data || []);
        }
      }
    } catch (error) {
      console.error('Failed to load existing images:', error);
    }
  };

  // 处理文件上传
  const handleFileUpload = async (files: FileList | null) => {
    if (!files) return;
    if (!course?.id) {
      setError('Please save course information first before uploading images');
      return;
    }

    const remainingSlots = 8 - totalImageCount;
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    if (filesToProcess.length === 0) {
      setError('Maximum upload limit reached (8 images)');
      return;
    }

    // 验证文件类型和大小
    const invalidFiles = filesToProcess.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      return !isValidType || !isValidSize;
    });

    if (invalidFiles.length > 0) {
      setError('Please upload valid image files (JPG, PNG, WebP, max 5MB)');
      return;
    }

    // 为每个文件创建上传状态
    const newUploadingImages: UploadingImage[] = filesToProcess.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
      status: 'uploading'
    }));

    setUploadingImages(prev => [...prev, ...newUploadingImages]);
    setError(null);

    // 逐个上传文件
    for (const uploadingImage of newUploadingImages) {
      try {
        const result = await apiService.uploadGolfCourseImage(course.id, uploadingImage.file);
        
        if (result.success && result.data) {
          // 更新上传状态为完成
          setUploadingImages(prev => prev.map(img => 
            img.id === uploadingImage.id 
              ? { ...img, status: 'completed', progress: 100, url: result.data!.url, imageId: result.data!.imageId }
              : img
          ));
          
          // 重新加载图片列表
          setTimeout(() => {
            loadExistingImages(course.id);
            setUploadingImages(prev => prev.filter(img => img.id !== uploadingImage.id));
          }, 1000);
        } else {
          // 上传失败
          setUploadingImages(prev => prev.map(img => 
            img.id === uploadingImage.id 
              ? { ...img, status: 'error', error: result.error || '上传失败' }
              : img
          ));
        }
      } catch (error) {
        setUploadingImages(prev => prev.map(img => 
          img.id === uploadingImage.id 
            ? { ...img, status: 'error', error: '网络错误' }
            : img
        ));
      }
    }
  };

  // 删除图片
  const handleDeleteImage = async (imageId: number) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    setDeletingImageIds(prev => new Set(Array.from(prev).concat(imageId)));
    
    try {
      const result = await apiService.deleteGolfCourseImage(imageId);
      if (result.success) {
        setExistingImages(prev => prev.filter(img => img.id !== imageId));
        setError(null);
      } else {
        setError(result.error || 'Failed to delete image');
      }
    } catch (error) {
      setError('Failed to delete image: Network error');
    } finally {
      setDeletingImageIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  };

  // 移除正在上传的图片
  const removeUploadingImage = (id: string) => {
    setUploadingImages(prev => prev.filter(img => img.id !== id));
  };

  // 拖拽处理
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) return;
    if (!course?.id) return;

    setIsReordering(true);
    
    try {
      // 重新排序图片
      const reorderedImages = [...existingImages];
      const [movedImage] = reorderedImages.splice(draggedImageIndex, 1);
      reorderedImages.splice(dropIndex, 0, movedImage);

      // 生成新的顺序
      const imageIds = reorderedImages.map(img => img.id);
      
      const result = await apiService.reorderGolfCourseImages(imageIds);
      
      if (result.success) {
        setExistingImages(reorderedImages);
      } else {
        setError(result.error || 'Failed to reorder images');
      }
    } catch (error) {
      setError('Failed to reorder images: Network error');
    } finally {
      setIsReordering(false);
      setDraggedImageIndex(null);
      setDragOverIndex(null);
    }
  };

  // 文件拖拽上传
  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  // 只在编辑模式显示
  if (!course?.id) return null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Course Images {totalImageCount > 0 && `(${totalImageCount}/8)`}
      </label>
      <div className="space-y-4">
        {/* 现有图片网格 */}
        {existingImages.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {existingImages.map((image, index) => (
              <div
                key={image.id}
                className={`relative group cursor-move transition-all ${
                  dragOverIndex === index ? 'ring-2 ring-indigo-500 scale-105' : ''
                } ${
                  draggedImageIndex === index ? 'opacity-50' : ''
                } ${
                  isReordering ? 'pointer-events-none' : ''
                }`}
                draggable={!isReordering}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, index)}
              >
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={image.url}
                    alt={`Golf course view ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {deletingImageIds.has(image.id) && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                
                <div className="absolute top-2 right-2 flex space-x-1">
                  <button
                    type="button"
                    onClick={() => handleDeleteImage(image.id)}
                    disabled={deletingImageIds.has(image.id) || isReordering}
                    className="p-1 bg-red-600 text-white rounded-full transition-all hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md md:opacity-0 md:group-hover:opacity-100"
                    title="Delete image"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                
                <div className="absolute top-2 left-2 bg-black bg-opacity-50 rounded p-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <GripVertical className="w-4 h-4 text-white drop-shadow-md" />
                </div>
                
                {index === 0 && (
                  <div className="absolute bottom-2 right-2">
                    <span className="px-2 py-1 bg-indigo-600 text-white text-xs rounded shadow-md">
                      Cover
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Uploading images */}
        {uploadingImages.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {uploadingImages.map((uploadImg) => (
              <div key={uploadImg.id} className="relative">
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {uploadImg.status === 'uploading' && (
                    <>
                      <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                      <div className="absolute bottom-2 left-2 right-2">
                        <div className="bg-white bg-opacity-75 rounded px-2 py-1">
                          <div className="text-xs text-gray-600">Uploading...</div>
                        </div>
                      </div>
                    </>
                  )}
                  {uploadImg.status === 'completed' && uploadImg.url && (
                    <>
                      <img
                        src={uploadImg.url}
                        alt="Uploaded"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                    </>
                  )}
                  {uploadImg.status === 'error' && (
                    <div className="text-center p-4">
                      <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                      <p className="text-sm text-red-600">{uploadImg.error}</p>
                    </div>
                  )}
                </div>
                
                <button
                  type="button"
                  onClick={() => removeUploadingImage(uploadImg.id)}
                  className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* 上传区域 */}
        {totalImageCount < 8 && (
          <div 
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
              isDragOver 
                ? 'border-indigo-500 bg-indigo-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <div className="space-y-2">
              <div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-indigo-600 hover:text-indigo-500 font-medium"
                >
                  {totalImageCount === 0 ? 'Upload Images' : 'Add More Images'}
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  Drag & drop images here or click to upload
                </p>
                <p className="text-xs text-gray-400">
                  Supports JPG, PNG, WebP formats, max 5MB per file
                </p>
                <p className="text-xs text-gray-400">
                  Upload up to 8 images, first image will be cover
                </p>
              </div>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
          </div>
        )}

        {/* 最大图片数提示 */}
        {totalImageCount >= 8 && (
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              Maximum image upload limit reached. Delete existing images to add new ones.
            </p>
          </div>
        )}
        
        {/* 排序提示 */}
        {existingImages.length > 1 && (
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              💡 Tip: Drag images to reorder, first image will be displayed as cover
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageManagementSection;